import { useTranslation } from 'react-i18next';
import { FC, useEffect, useState } from 'react';

import { Sizes } from '@common/types';
import { Modal } from '@base-app';
import { DateRangeSelection } from '@builder';
import {
  STUDY_INSIGHTS_TRANSLATION,
  SEQUENCED_SAMPLES_STATUS_FILTER,
  RECEIVED_SAMPLES_STATUS_FILTER,
} from '@modules/study-insights/constants';
import { useQuerySequencedSamples } from '@modules/study-insights/hooks';
import { useSequenceSamplesStore } from '@modules/study-insights/store';
import {
  ReceivedSamples,
  SampleCollectionLocation,
  SequencedSamples,
  SampleSerieType,
  SequencingSamplesSerie,
} from '@modules/study-insights/types';
import { SampleViewGraphByCollectionLocationModalProps } from './SampleViewGraphByCollectionLocationModal.types';
import { ViewGraphFilterProps } from '../SampleViewGraph/SampleViewGraphFilter/SampleViewGraphFilter.types';
import { SampleTypeSelectId } from '../../Demographics/Demographics.types';
import { PillId } from '../../CollectionAndSequencing.types';
import SampleViewGraph from '../SampleViewGraph/SampleViewGraph';

import { getFormattedDate } from '@common/utils';

const SampleViewGraphByCollectionLocationModal: FC<SampleViewGraphByCollectionLocationModalProps> = ({
  collectionLocation,
  onClose,
}) => {
  const { t } = useTranslation([STUDY_INSIGHTS_TRANSLATION]);
  const { selectedDateRange: selectedDateRangeStore, selectedPill } = useSequenceSamplesStore();
  const [endDate, setEndDate] = useState<Date>();
  const [graphData, setGraphData] = useState<SequencingSamplesSerie[]>();
  const [graphTitle, setGraphTitle] = useState<string>('');
  const [sampleType, setSampleType] = useState<SampleSerieType>();
  const [selectedDateRange, setSelectedDateRange] = useState<DateRangeSelection | undefined>(selectedDateRangeStore);
  const [sequenceData, setSequenceData] = useState<SequencedSamples | ReceivedSamples>();
  const [startDate, setStartDate] = useState<Date | undefined>();

  const { data: sequencesSamplesData } = useQuerySequencedSamples(
    {
      startDate: getFormattedDate(startDate, true),
      endDate: getFormattedDate(endDate, true),
      sampleStatus: SEQUENCED_SAMPLES_STATUS_FILTER,
    },
    {
      enabled: selectedPill === PillId.SEQUENCED,
      staleTime: Infinity,
    },
  );

  const { data: receivedSamplesData } = useQuerySequencedSamples(
    {
      startDate: getFormattedDate(startDate, true),
      endDate: getFormattedDate(endDate, true),
      sampleStatus: RECEIVED_SAMPLES_STATUS_FILTER,
    },
    {
      enabled: selectedPill === PillId.RECEIVED,
      staleTime: Infinity,
    },
  );
  const handleClose = (): void => {
    onClose?.();
  };

  const handleFilterChange = (filter: ViewGraphFilterProps): void => {
    const { dateRange, type } = filter;

    setSelectedDateRange(dateRange);
    setSampleType((type as unknown as SampleTypeSelectId) !== SampleTypeSelectId.ALL ? type : undefined);
  };

  useEffect(() => {
    setStartDate(selectedDateRange?.startDate);
    setEndDate(selectedDateRange?.endDate);
  }, [selectedDateRange]);

  useEffect(() => {
    if (selectedPill === PillId.SEQUENCED) {
      if (collectionLocation === SampleCollectionLocation.IN_PERSON) {
        setGraphTitle(t('sequenced_samples.in_person_title'));
      } else {
        setGraphTitle(t('sequenced_samples.at_home_title'));
      }
    }

    if (selectedPill === PillId.RECEIVED) {
      if (collectionLocation === SampleCollectionLocation.IN_PERSON) {
        setGraphTitle(t('in_person_collection'));
      } else {
        setGraphTitle(t('at_home_collection'));
      }
    }
  }, [collectionLocation]);

  useEffect(() => {
    setSequenceData(sequencesSamplesData);
  }, [sequencesSamplesData]);

  useEffect(() => {
    setSequenceData(receivedSamplesData);
  }, [receivedSamplesData]);

  useEffect(() => {
    if (!sequenceData) {
      return;
    }

    // Type assertion to help TypeScript understand the filter
    const filteredData = (sequenceData.series as SequencingSamplesSerie[])?.filter((item) => {
      let filtered = true;

      if (sampleType !== undefined) {
        filtered = item.type === sampleType;
      }

      return filtered && item.collectionLocation === collectionLocation;
    });
    setGraphData(filteredData);
  }, [sequenceData, sampleType]);

  useEffect(() => {
    setStartDate(selectedDateRangeStore?.startDate);
    setEndDate(selectedDateRangeStore?.endDate);
  }, [selectedDateRangeStore]);

  return (
    <Modal show={true} onClose={handleClose} title={graphTitle} size={Sizes.XXX_LARGE}>
      <SampleViewGraph
        selectedPill={selectedPill}
        displaySampleTypeFilter={true}
        selectedDateRange={selectedDateRange}
        data={graphData}
        onFilterChange={handleFilterChange}
        excludedSampleTypes={
          collectionLocation === SampleCollectionLocation.AT_HOME ? [SampleTypeSelectId.BLOOD_SAMPLES] : undefined
        }
      />
    </Modal>
  );
};

export default SampleViewGraphByCollectionLocationModal;
